import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
  HttpStatusCode,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, EMPTY, throwError, timer } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { LoggingService } from '../services/http/logging.service';
import { TosterService } from '../services/toster/toster.service';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class HttpErrorInterceptor implements HttpInterceptor {
  private static readonly DEFAULT_ERROR_MESSAGE =
    'Oops.. something went wrong!';
  private static readonly RETRY_DELAY_MS = 1000;

  constructor(
    private readonly toster: TosterService,
    private readonly logger: LoggingService,
    private readonly router: Router
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      retry({ count: 1, delay: this.shouldRetry }),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    const { status } = error;

    if (status === HttpStatusCode.Unauthorized) {
      this.logger.log('Authorization required');
      return EMPTY;
    }

    if (status === HttpStatusCode.Forbidden) {
      return EMPTY;
    }

    if (this.shouldRedirectToImIntro(error)) {
      this.router.navigate(['static-pages/ka-intro']);
      return EMPTY;
    }

    if (status === HttpStatusCode.Conflict) {
      return throwError(() => error);
    }

    this.displayErrorMessage(error);
    return EMPTY;
  }

  private shouldRetry(
    error: HttpErrorResponse
  ): Observable<number> | Observable<never> {
    return error.status === HttpStatusCode.Unauthorized
      ? timer(HttpErrorInterceptor.RETRY_DELAY_MS)
      : throwError(() => error);
  }

  private shouldRedirectToImIntro(error: HttpErrorResponse): boolean {
    if (error.status !== HttpStatusCode.BadRequest) return false;

    const params = new URLSearchParams(window.location.search);
    const isInspectionManager = params.get('integration-type') === 'IM';

    return isInspectionManager && this.isSessionPath(error.url);
  }

  private isSessionPath(url: string | null): boolean {
    if (!url) return false;
    try {
      return new URL(url).pathname === '/session';
    } catch {
      return false;
    }
  }

  private displayErrorMessage(error: HttpErrorResponse): void {
    if (error.error instanceof ErrorEvent) {
      this.toster.error(HttpErrorInterceptor.DEFAULT_ERROR_MESSAGE);
      return;
    }

    if (error.status === HttpStatusCode.BadRequest) {
      const message = this.extractValidationErrorMessage(error);
      this.toster.error(message);
      return;
    }

    const message =
      error.error?.errorMessage ?? HttpErrorInterceptor.DEFAULT_ERROR_MESSAGE;
    this.toster.error(message);
  }

  private extractValidationErrorMessage(error: HttpErrorResponse): string {
    return (
      error.error?.errors?.Name?.[0] ??
      error.error?.title ??
      HttpErrorInterceptor.DEFAULT_ERROR_MESSAGE
    );
  }
}
